/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import { observable, when } from 'mobx';
import type { TranslatorProps } from 'react-i18next';

import { ElectroButton } from 'components/ExtendedUiElements/customized.js';
import CentralForm from 'components/CentralForm/CentralForm';
import { Roles } from 'permissions/defs';

import styles from 'common-scss/formstyles.module.scss';
import { errorHandlerService } from '../../services/ServiceRegistry';

@translate()
@inject('authenticationStore', 'userStore', 'apiService')
@observer
export default class ValidateJwtLink extends React.Component<*> {
  @observable response: ?Object;

  /**
   * Prevent duplicate network calls due to double-mounts / StrictMode.
   */
  _loginStarted: boolean = false;

  async componentDidMount(): Promise<void> {
    const storageKey = 'b2c_jwt_exchange_in_progress';

    try {
      // Bridge-only: token is stored in HttpOnly cookie, never in URL.
      this.removeAuthNoiseFromUrlButKeepCategory();

      if (this.props.authenticationStore.isUserLoggedIn()) {
        this.goToRoute();
        return;
      }

      if (this._loginStarted) return;
      this._loginStarted = true;

      const sentinel = 'cookie-bridge-only';
      if (window.sessionStorage.getItem(storageKey) === sentinel) {
        return;
      }
      window.sessionStorage.setItem(storageKey, sentinel);

      // Bridge-only: backend reads token from cookie
      this.response = await this.props.apiService.loginWithJwtToken();

      if (this.response && this.response.success) {
        await this.props.userStore.getActiveUser();

        when(
          () =>
            typeof this.props.userStore.userRoleId !== 'undefined' &&
            this.props.userStore.userRoleId !== Roles.LOGIN,
          () => {
            window.sessionStorage.removeItem(storageKey);
            this.goToRoute();
          }
        );
      } else {
        window.sessionStorage.removeItem(storageKey);
        this.response = this.response || { success: false };
      }
    } catch (e) {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (_) {}

      this.response = e;
      errorHandlerService.handleException(e);
    }
  }

  goToRoute() {
    const params = new URL(document.location).searchParams;
    const category = params.get('category')?.toLowerCase();

    switch (category) {
      case 'asset':
        this.props.history.push('/assets');
        break;
      case 'order':
        this.props.history.push('/orders');
        break;
      default:
        this.props.history.push('/');
    }
  }

  /**
   * Defensive cleanup of legacy auth params.
   */
  removeAuthNoiseFromUrlButKeepCategory() {
    const url = new URL(window.location.href);
    url.searchParams.delete('_erToken');
    url.searchParams.delete('token');

    const clean =
      url.pathname +
      (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
      url.hash;

    window.history.replaceState({}, document.title, clean);
  }

  /**
   * Normalize error status from different shapes.
   */
  getErrorStatus(): ?number {
    const err: any = this.response;
    return err?.status || err?.response?.status || err?.data?.status || null;
  }

  /**
   * Decide which i18n key to show.
   */
  getErrorMessageKey(): string {
    const status = this.getErrorStatus();

    // User exists but cannot access portal (no role / disabled / not onboarded)
    if (status === 403) {
      return 'general:document_titles.authentication.user_not_active';
    }

    // Token/session missing or expired
    if (status === 400 || status === 401) {
      return 'general:document_titles.authentication.token_invalid';
    }

    // Fallback
    return 'general:document_titles.authentication.generic_error';
  }

  render() {
    const { t }: TranslatorProps = this.props;

    if (this.response && !this.response.success) {
      const messageKey = this.getErrorMessageKey();

      return (
        <CentralForm>
          <div>
            <h1>{t('general:error')}</h1>
            <p>{t(messageKey)}</p>

            <a href={process.env.REACT_APP_MARKETING_URL} className={styles['button-anchor']}>
              <ElectroButton label={t('general:er_online_title')} color="primary" />
            </a>
          </div>
        </CentralForm>
      );
    }

    return (
      <CentralForm>
        <div>
          <h1>{t('general:accounts.validating')}</h1>
        </div>
      </CentralForm>
    );
  }
}
